/**
 * MilkDrop presets via Butterchurn (ADR-0006). Butterchurn draws with its own WebGL2 context on
 * an OffscreenCanvas; each frame we feed it our audio and copy its picture into the layer.
 *
 * Spike S2 findings, encoded here:
 * - Butterchurn assumes `window` and `document` (globals for its equation helpers, a 2D canvas
 *   for title text). In the render worker we shim both before loading it.
 * - It accepts external audio via render({ audioLevels }) and skips its own analysers when it
 *   is given no AudioContext.
 * - Presets contain JavaScript (equations are compiled with new Function). So we only load the
 *   pinned, integrity-checked community pack, never arbitrary preset files (ADR-0007).
 */
import { F, WAVEFORM_SAMPLES } from '../../../shared/audioFrame';
import type { VisualizationDef } from '../../../shared/viz';
import { uniform, unbindUnits, type GL, type RenderTarget } from '../gl/gl';
import { UNIT_INPUT, type FrameContext } from '../frame';
import type { PaletteBinding, ProgramCache, VizInstance } from '../viz/instances';

interface ButterchurnVisualizer {
  loadPreset(preset: unknown, blendTime: number): void;
  setRendererSize(w: number, h: number, opts?: Record<string, number>): void;
  render(opts: {
    audioLevels: {
      timeByteArray: Uint8Array;
      timeByteArrayL: Uint8Array;
      timeByteArrayR: Uint8Array;
    };
  }): void;
}

type ButterchurnModule = {
  createVisualizer(
    ctx: null,
    canvas: OffscreenCanvas | HTMLCanvasElement,
    opts: Record<string, number>,
  ): ButterchurnVisualizer;
};

let modulePromise: Promise<ButterchurnModule> | null = null;
let presets: Record<string, unknown> = {};

/** Minimal stand-in for HTMLImageElement in workers: decodes `src` into an ImageBitmap. */
class WorkerImage {
  onload: (() => void) | null = null;
  width = 0;
  height = 0;
  bitmap: ImageBitmap | null = null;
  set src(url: string) {
    void fetch(url)
      .then((r) => r.blob())
      .then((b) => createImageBitmap(b))
      .then((bmp) => {
        this.bitmap = bmp;
        this.width = bmp.width;
        this.height = bmp.height;
        this.onload?.();
      })
      .catch(() => undefined);
  }
}

/** Make Butterchurn's browser assumptions hold inside a worker. */
function shimGlobals(): void {
  const g = globalThis as Record<string, unknown>;
  if (!g.window) g.window = globalThis;
  if (!g.document) {
    g.document = {
      createElement: () => new OffscreenCanvas(1, 1),
    };
  }
  if (!g.Image) {
    g.Image = WorkerImage;
    // Butterchurn hands its Image objects to texImage2D; unwrap ours to the decoded bitmap.
    const proto = WebGL2RenderingContext.prototype as unknown as {
      texImage2D: (...a: unknown[]) => void;
    };
    const original = proto.texImage2D;
    proto.texImage2D = function (this: unknown, ...args: unknown[]) {
      for (let i = 0; i < args.length; i++)
        if (args[i] instanceof WorkerImage) args[i] = (args[i] as WorkerImage).bitmap;
      return original.apply(this, args);
    };
  }
}

function loadModule(): Promise<ButterchurnModule> {
  shimGlobals();
  // CommonJS/UMD interop: depending on the bundler the class sits at m, m.default or m.default.default.
  modulePromise ??= import('butterchurn').then((m: unknown) => {
    let cur = m as { default?: unknown; createVisualizer?: unknown } | undefined;
    for (let i = 0; i < 3 && cur && typeof cur.createVisualizer !== 'function'; i++) {
      cur = cur.default as typeof cur;
    }
    if (!cur || typeof cur.createVisualizer !== 'function')
      throw new Error('Butterchurn failed to load');
    return cur as unknown as ButterchurnModule;
  });
  return modulePromise;
}

/** Evaluate the verified preset pack (UMD bundle) and return preset names. */
export function installPresetPack(code: string): string[] {
  shimGlobals();
  const holder: Record<string, unknown> = {};
  // The UMD wrapper assigns to `self` when there is no module system.
  new Function('self', `${code}\n;`)(holder);
  const pack = (holder.butterchurnPresets ?? holder.butterchurnPresetsMinimal) as
    { getPresets?: () => Record<string, unknown> } | undefined;
  presets = pack?.getPresets?.() ?? {};
  return Object.keys(presets).sort((a, b) => a.localeCompare(b));
}

export function hasPreset(name: string): boolean {
  return name in presets;
}

const COPY_FS = `#version 300 es
precision highp float;
in vec2 vUv;
uniform sampler2D uTex;
out vec4 fragColor;
void main() { fragColor = vec4(texture(uTex, vUv).rgb, 1.0); }`;

export class ButterchurnViz implements VizInstance {
  private canvas: OffscreenCanvas | null = null;
  private viz: ButterchurnVisualizer | null = null;
  private readonly tex: WebGLTexture;
  private error: string | null = null;
  private width = 0;
  private height = 0;
  private loaded = false;
  private readonly bytes = new Uint8Array(1024);

  constructor(
    private readonly gl: GL,
    private readonly cache: ProgramCache,
    private readonly def: VisualizationDef,
  ) {
    this.tex = gl.createTexture()!;
    void this.init();
  }

  private async init(): Promise<void> {
    try {
      const preset = presets[this.def.source ?? ''];
      if (!preset) {
        this.error = 'MilkDrop preset pack not loaded';
        return;
      }
      const bc = await loadModule();
      this.canvas = new OffscreenCanvas(640, 360);
      this.viz = bc.createVisualizer(null, this.canvas, {
        width: 640,
        height: 360,
        pixelRatio: 1,
        textureRatio: 1,
      });
      this.viz.loadPreset(preset, 0);
      this.width = 640;
      this.height = 360;
      this.loaded = true;
    } catch (err) {
      this.error = String(err);
    }
  }

  get ready(): boolean {
    return this.loaded;
  }

  get failed(): boolean {
    return this.error !== null;
  }

  get errorMessage(): string | null {
    return this.error;
  }

  render(
    fc: FrameContext,
    _params: Float32Array,
    _palette: PaletteBinding,
    target: RenderTarget,
  ): void {
    const viz = this.viz;
    const canvas = this.canvas;
    if (!viz || !canvas) return;
    // Butterchurn is CPU-heavy (per-vertex equations in JS); cap its internal resolution.
    const scale = Math.min(1, 1280 / target.width);
    const w = Math.max(64, Math.round(target.width * scale));
    const h = Math.max(36, Math.round(target.height * scale));
    if (w !== this.width || h !== this.height) {
      canvas.width = w;
      canvas.height = h;
      viz.setRendererSize(w, h);
      this.width = w;
      this.height = h;
    }
    // Feed our waveform as 8-bit time-domain samples (what Butterchurn's analyser would give).
    const frame = fc.frameData;
    const b = this.bytes;
    for (let i = 0; i < b.length; i++) {
      const v = frame[(F.WAVEFORM + (i * WAVEFORM_SAMPLES) / b.length) | 0]!;
      b[i] = 128 + Math.max(-127, Math.min(127, Math.round(v * 100)));
    }
    viz.render({ audioLevels: { timeByteArray: b, timeByteArrayL: b, timeByteArrayR: b } });

    const gl = this.gl;
    gl.activeTexture(gl.TEXTURE0 + UNIT_INPUT);
    gl.bindTexture(gl.TEXTURE_2D, this.tex);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, canvas);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    const e = this.cache.get('builtin:copy', () => COPY_FS);
    if (!e.program) return;
    gl.bindFramebuffer(gl.FRAMEBUFFER, target.fbo);
    gl.viewport(0, 0, target.width, target.height);
    gl.bindVertexArray(fc.vao);
    gl.useProgram(e.program.program);
    gl.uniform1i(uniform(gl, e.program, 'uTex'), UNIT_INPUT);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    unbindUnits(gl, UNIT_INPUT, UNIT_INPUT);
  }

  dispose(): void {
    this.gl.deleteTexture(this.tex);
    this.viz = null;
    this.canvas = null;
  }
}
